import {faPlus, faTrash} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Alert, Button, Checkbox, Form, Input, Radio, Select, Space, Switch} from 'antd';

import {Locale} from '../../../../../provider/locale/locale';
import {useLocale} from '../../../../../provider/locale/locale-hook';
import {LangKeyType} from '../../../../../provider/locale/translation/type';
import {useSnackbar} from '../../../../../provider/snackbar/snackbar-hook';
import {aiMonitoringSignalLangKeyMap} from '../../../../../service/ai-brand-monitoring/ai-brand-monitoring-const';
import {
    getAiMonitoringConfigError,
    useAiMonitoringAvailableCompetitors,
    useUpdateAiMonitoringConfig,
} from '../../../../../service/ai-brand-monitoring/ai-brand-monitoring-hook';
import {
    AI_MONITORING_MAX_ASPECTS,
    AiMonitoringAspectModeEnum,
    AiMonitoringConfigType,
    AiMonitoringConfigUpdatePayloadType,
    AiMonitoringVisibilitySignalEnum,
} from '../../../../../service/ai-brand-monitoring/ai-brand-monitoring-type';
import {useReviewsAspects} from '../../../../../service/reviews/reviews-tags';

import * as styles from './settings-form.scss';

type SettingsFormValuesType = {
    enabled: boolean;
    prompts: Array<{id?: number; text: string; isActive: boolean}>;
    competitors: Array<number>;
    visibilitySignals: Array<AiMonitoringVisibilitySignalEnum>;
    aspectMode: AiMonitoringAspectModeEnum;
    customAspectIds: Array<number>;
};

type PropsType = {
    brandId: number;
    config: AiMonitoringConfigType;
    onSaved?: () => void;
};

function mapConfigErrorLangKey(code: string): LangKeyType {
    switch (code) {
        case 'unsupported_model':
            return 'AI_BRAND_MONITORING__ERROR__UNSUPPORTED_MODEL';
        case 'monitoring_not_configured':
            return 'AI_BRAND_MONITORING__ERROR__NOT_CONFIGURED';
        case 'unknown_competitor':
            return 'AI_BRAND_MONITORING__ERROR__UNKNOWN_COMPETITOR';
        case 'invalid_visibility_signals':
            return 'AI_BRAND_MONITORING__ERROR__INVALID_SIGNALS';
        case 'aspects_required':
            return 'AI_BRAND_MONITORING__ERROR__ASPECTS_REQUIRED';
        case 'too_many_aspects':
            return 'AI_BRAND_MONITORING__ERROR__TOO_MANY_ASPECTS';
        default:
            return 'AI_BRAND_MONITORING__ERROR__LIMIT_EXCEEDED';
    }
}

function buildConfigPayload(
    values: SettingsFormValuesType,
    aspectTitleById: Map<number, string>
): AiMonitoringConfigUpdatePayloadType {
    const isCustom = values.aspectMode === AiMonitoringAspectModeEnum.custom;

    return {
        enabled: values.enabled,
        prompts: (values.prompts ?? []).map((prompt) => ({
            ...(prompt.id ? {id: prompt.id} : {}),
            text: prompt.text,
            isActive: prompt.isActive ?? true,
        })),
        competitors: (values.competitors ?? []).map((competitorRefId) => ({
            competitorRefId,
        })),
        visibilitySignals: values.visibilitySignals ?? [],
        aspectMode: values.aspectMode,
        customAspects: isCustom
            ? (values.customAspectIds ?? []).map((tagId) => ({tagId, title: aspectTitleById.get(tagId) ?? ''}))
            : [],
    };
}

// Custom aspects come from the brand's review aspects (title -> representative tag id),
// merged with the config's already-pinned aspects so the current selection always has options.
function buildAspectOptions(
    reviewAspects: Array<{ids: Array<number>; title: string}> | undefined,
    config: AiMonitoringConfigType
): {aspectTitleById: Map<number, string>; aspectOptions: Array<{label: string; value: number}>} {
    const aspectTitleById = new Map<number, string>();

    (reviewAspects ?? []).forEach((aspect) => {
        const tagId = aspect.ids[0];

        if (typeof tagId === 'number') {
            aspectTitleById.set(tagId, aspect.title);
        }
    });
    config.customAspects.forEach((aspect) => aspectTitleById.set(aspect.tagId, aspect.title));

    const aspectOptions = [...aspectTitleById.entries()].map(([value, label]) => ({label, value}));

    return {aspectTitleById, aspectOptions};
}

function buildSignalOptions(
    getLocalizedString: ReturnType<typeof useLocale>['getLocalizedString']
): Array<{label: string; value: AiMonitoringVisibilitySignalEnum}> {
    return Object.values(AiMonitoringVisibilitySignalEnum).map((signal) => ({
        label: getLocalizedString(aiMonitoringSignalLangKeyMap[signal]),
        value: signal,
    }));
}

export function SettingsForm(props: PropsType): JSX.Element {
    const {brandId, config, onSaved} = props;
    const {getLocalizedString} = useLocale();
    const {snackbar} = useSnackbar();
    const [form] = Form.useForm<SettingsFormValuesType>();

    const {mutate, isLoading} = useUpdateAiMonitoringConfig(brandId);
    const {data: availableCompetitors} = useAiMonitoringAvailableCompetitors(brandId);

    const aspectMode = Form.useWatch('aspectMode', form) ?? config.aspectMode;
    const {data: reviewAspects} = useReviewsAspects(aspectMode === AiMonitoringAspectModeEnum.custom);

    const canManage = config.permissions.canManage;
    const {maxPrompts, maxCompetitors} = config.limits;

    const competitorOptions = (availableCompetitors ?? []).map((competitor) => ({
        label: competitor.name,
        value: competitor.id,
    }));

    const hasAvailableCompetitors = competitorOptions.length > 0;

    const {aspectTitleById, aspectOptions} = buildAspectOptions(reviewAspects, config);
    const hasReviewAspects = aspectOptions.length > 0;
    const signalOptions = buildSignalOptions(getLocalizedString);

    const initialValues: SettingsFormValuesType = {
        enabled: config.enabled,
        prompts: config.prompts.map((prompt) => ({id: prompt.id, text: prompt.text, isActive: prompt.isActive})),
        competitors: config.competitors
            .map((competitor) => competitor.competitorRefId)
            .filter((refId): refId is number => refId !== null),
        visibilitySignals: config.visibilitySignals,
        aspectMode: config.aspectMode,
        customAspectIds: config.customAspects.map((aspect) => aspect.tagId),
    };

    function handleSaveError(error: unknown): void {
        const configError = getAiMonitoringConfigError(error);

        if (!configError) {
            snackbar.error(getLocalizedString('AI_BRAND_MONITORING__SETTINGS__SAVE_ERROR'));

            return;
        }

        const message = getLocalizedString(mapConfigErrorLangKey(configError.code));

        snackbar.error(configError.detail ? `${message} ${configError.detail}` : message);
    }

    function handleFinish(values: SettingsFormValuesType): void {
        mutate(buildConfigPayload(values, aspectTitleById), {
            onSuccess: () => {
                snackbar.success(getLocalizedString('AI_BRAND_MONITORING__SETTINGS__SAVED'));
                onSaved?.();
            },
            onError: handleSaveError,
        });
    }

    return (
        <Form<SettingsFormValuesType>
            disabled={!canManage}
            form={form}
            initialValues={initialValues}
            layout="vertical"
            onFinish={handleFinish}
        >
            {!canManage ? (
                <Alert
                    className={styles.settings_form__alert}
                    description={getLocalizedString('AI_BRAND_MONITORING__SETTINGS__READ_ONLY')}
                    showIcon
                    type="info"
                />
            ) : null}

            <Form.Item
                label={<Locale stringKey="AI_BRAND_MONITORING__SETTINGS__ENABLED" />}
                name="enabled"
                valuePropName="checked"
            >
                <Switch />
            </Form.Item>

            <Alert
                className={styles.settings_form__alert}
                description={
                    <div>
                        <div>{getLocalizedString('AI_BRAND_MONITORING__SETTINGS__PROMPT_HINT_TITLE')}</div>
                        <ul className={styles.settings_form__hint_list}>
                            <li>{getLocalizedString('AI_BRAND_MONITORING__SETTINGS__PROMPT_HINT_EXAMPLE_1')}</li>
                            <li>{getLocalizedString('AI_BRAND_MONITORING__SETTINGS__PROMPT_HINT_EXAMPLE_2')}</li>
                            <li>{getLocalizedString('AI_BRAND_MONITORING__SETTINGS__PROMPT_HINT_EXAMPLE_3')}</li>
                        </ul>
                        <div className={styles.settings_form__hint_note}>
                            {getLocalizedString('AI_BRAND_MONITORING__SETTINGS__PROMPT_HINT_NO_BRAND')}
                        </div>
                    </div>
                }
                showIcon
                type="info"
            />

            <Form.List name="prompts">
                {(fields, {add, remove}) => (
                    <div>
                        <div className={styles.settings_form__list_label}>
                            <Locale stringKey="AI_BRAND_MONITORING__SETTINGS__PROMPTS" />
                        </div>
                        {fields.map((field) => (
                            <Space align="baseline" className={styles.settings_form__prompt_row} key={field.key}>
                                <Form.Item
                                    className={styles.settings_form__prompt_field}
                                    name={[field.name, 'text']}
                                    rules={[
                                        {
                                            required: true,
                                            message: getLocalizedString(
                                                'AI_BRAND_MONITORING__SETTINGS__PROMPT_REQUIRED'
                                            ),
                                        },
                                    ]}
                                >
                                    <Input.TextArea
                                        autoSize={{minRows: 1, maxRows: 4}}
                                        placeholder={getLocalizedString(
                                            'AI_BRAND_MONITORING__SETTINGS__PROMPT_PLACEHOLDER'
                                        )}
                                    />
                                </Form.Item>
                                <Form.Item name={[field.name, 'isActive']} valuePropName="checked">
                                    <Switch size="small" />
                                </Form.Item>
                                {canManage ? (
                                    <Button
                                        icon={<FontAwesomeIcon icon={faTrash} />}
                                        onClick={() => remove(field.name)}
                                        type="text"
                                    />
                                ) : null}
                            </Space>
                        ))}
                        <Button
                            disabled={!canManage || fields.length >= maxPrompts}
                            icon={<FontAwesomeIcon icon={faPlus} />}
                            onClick={() => add({text: '', isActive: true})}
                            type="dashed"
                        >
                            <Locale stringKey="AI_BRAND_MONITORING__SETTINGS__ADD_PROMPT" />
                        </Button>
                        {fields.length >= maxPrompts ? (
                            <div className={styles.settings_form__limit_note}>
                                <Locale stringKey="AI_BRAND_MONITORING__SETTINGS__PROMPTS_LIMIT" />
                            </div>
                        ) : null}
                    </div>
                )}
            </Form.List>

            {hasAvailableCompetitors ? (
                <Form.Item
                    className={styles.settings_form__section}
                    label={<Locale stringKey="AI_BRAND_MONITORING__SETTINGS__COMPETITORS" />}
                    name="competitors"
                >
                    <Select
                        maxCount={maxCompetitors}
                        mode="multiple"
                        options={competitorOptions}
                        placeholder={getLocalizedString('AI_BRAND_MONITORING__SETTINGS__COMPETITORS_PLACEHOLDER')}
                    />
                </Form.Item>
            ) : (
                <Form.Item
                    className={styles.settings_form__section}
                    label={<Locale stringKey="AI_BRAND_MONITORING__SETTINGS__COMPETITORS" />}
                >
                    <div className={styles.settings_form__empty_note}>
                        <Locale stringKey="AI_BRAND_MONITORING__SETTINGS__COMPETITORS_NO_AVAILABLE" />
                    </div>
                </Form.Item>
            )}

            <Form.Item
                className={styles.settings_form__section}
                extra={getLocalizedString('AI_BRAND_MONITORING__SETTINGS__VISIBILITY_SIGNALS_HINT')}
                label={<Locale stringKey="AI_BRAND_MONITORING__SETTINGS__VISIBILITY_SIGNALS" />}
                name="visibilitySignals"
            >
                <Checkbox.Group options={signalOptions} />
            </Form.Item>

            <Form.Item
                className={styles.settings_form__section}
                label={<Locale stringKey="AI_BRAND_MONITORING__SETTINGS__ASPECT_MODE" />}
                name="aspectMode"
            >
                <Radio.Group>
                    <Space direction="vertical">
                        <Radio value={AiMonitoringAspectModeEnum.auto}>
                            <Locale stringKey="AI_BRAND_MONITORING__SETTINGS__ASPECT_MODE_AUTO" />
                        </Radio>
                        <Radio value={AiMonitoringAspectModeEnum.custom}>
                            <Locale stringKey="AI_BRAND_MONITORING__SETTINGS__ASPECT_MODE_CUSTOM" />
                        </Radio>
                    </Space>
                </Radio.Group>
            </Form.Item>

            {aspectMode === AiMonitoringAspectModeEnum.custom ? (
                <Form.Item
                    extra={getLocalizedString('AI_BRAND_MONITORING__SETTINGS__ASPECTS_HINT')}
                    name="customAspectIds"
                >
                    {hasReviewAspects ? (
                        <Select
                            maxCount={AI_MONITORING_MAX_ASPECTS}
                            mode="multiple"
                            options={aspectOptions}
                            placeholder={getLocalizedString('AI_BRAND_MONITORING__SETTINGS__ASPECTS_PLACEHOLDER')}
                        />
                    ) : (
                        <div className={styles.settings_form__empty_note}>
                            <Locale stringKey="AI_BRAND_MONITORING__SETTINGS__ASPECTS_NO_AVAILABLE" />
                        </div>
                    )}
                </Form.Item>
            ) : null}

            <Form.Item className={styles.settings_form__submit}>
                <Button disabled={!canManage} htmlType="submit" loading={isLoading} type="primary">
                    <Locale stringKey="AI_BRAND_MONITORING__SETTINGS__SAVE" />
                </Button>
            </Form.Item>
        </Form>
    );
}
